import React, { FC, useCallback } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import type { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { useTranslation } from 'app/lang';
import Dropdown, { ChangeHandler, Control } from 'app/widgets/Dropdown/Single';

import { ChangeSystemRank, changeSystemRank } from '../../../../actions/stakeTypes/system';
import { RankValue, SubmissionState } from '../../../../constants';
import { selectSubmission } from '../../../../selectors/submission';

import dictionary from './RankSelect.dictionary.json';

import styles from '../System.module.scss';

// ---

type SelectOption = { label: string; value: number };

type StoreProps = {
  disabled: boolean;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<ChangeSystemRank>;
};

type RankSelectProps = {
  value: number;
  of: number;
  className?: string;
};

const RankSelect: FC<RankSelectProps & DispatchProp & StoreProps> = ({
  value,
  of,
  className,
  disabled,
  dispatch,
}) => {
  const { t } = useTranslation();
  const items: SelectOption[] = [];

  const maxValue = of > RankValue.MAX ? RankValue.MAX + 1 : of;

  for (let n = RankValue.DEFAULT; n < maxValue; n++) {
    items.push({
      label: t(dictionary.optionLabel, { quantity: n, total: of }),
      value: n,
    });
  }

  const selectRankHandler: ChangeHandler<SelectOption> = useCallback((option) => {
    if (!option || !('value' in option)) {
      console.error('RankSelect -> selectRankHandler -> `option` not found', option);
      return;
    }

    if (value !== option.value) {
      dispatch(changeSystemRank(option.value));
    }
  }, [value]);

  const selectedValue = items.find((item) => item.value === value);

  return (
    <Dropdown<SelectOption>
      isSearchable={false}
      options={items}
      className={classNames(styles.dropdown, className)}
      value={selectedValue}
      disabled={disabled}
      indicatorSize={16}
      maxMenuHeight={125}
      onChange={selectRankHandler}
      components={{
        Control: (props) => (
          <Control {...props}
                   customInnerProps={{ title: t(dictionary.controlTitle) }}>
            {props.children}
          </Control>
        ),
      }} />
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => ({
  disabled: selectSubmission(state) !== SubmissionState.AWAITING,
});

export default connect(mapStateToProps)(RankSelect);
